import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import Pusher from 'pusher-js';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {
  private pusher!: Pusher;
  private channel: any;
  private userChannel: any;
  private roleChannel: any;
  private isConnected = false;
  private currentSocketId: string | null = null;
  private currentUserId: number | null = null;
  private currentUserIdForDisconnect: number | null = null; // Store user ID for disconnect
  // Notification subjects
  private notificationSubject = new BehaviorSubject<any>(null);
  private serviceStatusSubject = new BehaviorSubject<any>(null);
  private orderLocationSubject = new BehaviorSubject<any>(null);
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  private permissionsUpdatedSubject = new BehaviorSubject<any>(null);

  constructor(private http: HttpClient) {
    this.initializePusher();
  }

  private initializePusher(): void {
    try {
      this.pusher = new Pusher(environment.key_socket, {
  wsHost: environment.host_socket,
  wsPort: environment.port_socket,
  wssPort: environment.port_socket,
  forceTLS: environment.forceTLS,
  disableStats: true,
  enabledTransports: ['ws', 'wss'],
  cluster: environment.cluster_socket,
  authorizer: (channel: any, options: any) => {
    return {
      authorize: (socketId: string, callback: Function) => {
        const token = localStorage.getItem('token');
        const headers: any = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        this.http.post(`${environment.apiUrl}/broadcasting/auth`, {
          socket_id: socketId,
          channel_name: channel.name
        }, { headers }).subscribe({
          next: (response: any) => callback(null, response),
          error: (error) => callback(error, null)
        });
      }
    };
  }
});


      this.pusher.connection.bind('connected', () => {
        this.isConnected = true;
        this.connectionStatusSubject.next(true);
      });

      this.pusher.connection.bind('disconnected', () => {
        this.isConnected = false;
        this.connectionStatusSubject.next(false);
      });

      this.pusher.connection.bind('error', (error: any) => {
        this.connectionStatusSubject.next(false);
      });

      // Subscribe to general channels
      this.subscribeToGeneralChannels();
    } catch (error) {
    }
  }

  private subscribeToGeneralChannels(): void {
    // Subscribe to services channel
    this.channel = this.pusher.subscribe('services');
    
    this.channel.bind('service.status.updated', (data: any) => {
      this.serviceStatusSubject.next(data);
    });

    // Subscribe to orders channel
    const ordersChannel = this.pusher.subscribe('orders');
    
    ordersChannel.bind('order.location.updated', (data: any) => {
      this.orderLocationSubject.next(data);
    });

    // Note: Notifications are now handled via role-based channels
    // Public notifications channel is reserved for system-wide announcements
  }

  /**
   * Subscribe to role-based notifications
   */
  subscribeToRoleNotifications(userId: number, roleId: number): void {
    if (this.userChannel) {
      this.pusher.unsubscribe(this.userChannel.name);
    }

    // Subscribe to role-based channel (using public channel temporarily)
    this.userChannel = this.pusher.subscribe(`public-role.${roleId}`);
    
    
    this.userChannel.bind('notification.sent', (data: any) => {
      this.notificationSubject.next(data);
    });

    this.userChannel.bind('service.status.updated', (data: any) => {
      this.serviceStatusSubject.next(data);
    });

    this.userChannel.bind('permissions.updated', (data: any) => {
      this.permissionsUpdatedSubject.next(data);
    });

    // Add connection state logging
    this.userChannel.bind('pusher:subscription_succeeded', () => {
    });

    this.userChannel.bind('pusher:subscription_error', (error: any) => {
    });

    // Register connection with backend (still need user info for connection tracking)
    this.registerRoleConnection(userId, roleId);
    
  }

  /**
   * Subscribe to user-specific notifications (legacy method)
   */
  subscribeToUserNotifications(userId: number): void {
    if (this.userChannel) {
      this.pusher.unsubscribe(`private-user.${userId}`);
    }

    this.userChannel = this.pusher.subscribe(`private-user.${userId}`);
    
    this.userChannel.bind('notification.sent', (data: any) => {
      this.notificationSubject.next(data);
    });

    this.userChannel.bind('service.status.updated', (data: any) => {
      this.serviceStatusSubject.next(data);
    });

    // Register connection with backend
    this.registerConnection(userId);
    
  }

  /**
   * Get notification observable
   */
  getNotifications(): Observable<any> {
    return this.notificationSubject.asObservable();
  }

  /**
   * Get service status updates observable
   */
  getServiceStatusUpdates(): Observable<any> {
    return this.serviceStatusSubject.asObservable();
  }

  /**
   * Get order location updates observable
   */
  getOrderLocationUpdates(): Observable<any> {
    return this.orderLocationSubject.asObservable();
  }

  /**
   * Get permissions-updated events observable. Emits when an admin changes
   * this role's module/action permissions, so the sidebar can refresh live.
   */
  getPermissionsUpdates(): Observable<any> {
    return this.permissionsUpdatedSubject.asObservable();
  }

  /**
   * Get connection status observable
   */
  getConnectionStatus(): Observable<boolean> {
    return this.connectionStatusSubject.asObservable();
  }

  /**
   * Check if WebSocket is connected
   */
  isWebSocketConnected(): boolean {
    return this.isConnected;
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    if (this.pusher) {
      this.unregisterConnection();
      this.pusher.disconnect();
      this.isConnected = false;
      this.connectionStatusSubject.next(false);
    }
  }

  /**
   * Reconnect WebSocket
   */
  reconnect(): void {
    this.disconnect();
    setTimeout(() => {
      this.initializePusher();
    }, 1000);
  }

  /**
   * Register connection with backend
   */
  private registerConnection(userId: number): void {
    
    // Validate userId
    if (!userId || typeof userId !== 'number' || userId <= 0 || isNaN(userId)) {
      return;
    }
    
    this.currentSocketId = `socket_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    this.currentUserIdForDisconnect = userId; // Store for disconnect
    
    const payload = {
      user_id: userId,
      socket_id: this.currentSocketId
    };
    
    
    this.http.post(`${environment.apiUrl}/websocket/register-simple-connection`, payload).subscribe({
      next: (response: any) => {
      },
      error: (error) => {
      }
    });
  }

  /**
   * Register role-based connection with backend
   */
  private registerRoleConnection(userId: number, roleId: number): void {
    
    // Validate parameters
    if (!userId || typeof userId !== 'number' || userId <= 0 || isNaN(userId)) {
      return;
    }
    
    if (!roleId || typeof roleId !== 'number' || roleId <= 0 || isNaN(roleId)) {
      return;
    }
    
    this.currentSocketId = `socket_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    this.currentUserIdForDisconnect = userId; // Store for disconnect
    
    const payload = {
      user_id: userId,
      role_id: roleId,
      socket_id: this.currentSocketId,
      channel_name: `public-role.${roleId}`
    };
    
    
    this.http.post(`${environment.apiUrl}/websocket/register-role-connection`, payload).subscribe({
      next: (response: any) => {
      },
      error: (error) => {
      }
    });
  }

  /**
   * Unregister connection with backend
   */
  private unregisterConnection(): void {
    if (this.currentSocketId) {
      if (this.currentUserIdForDisconnect) {
        // Try full unregister with user_id first
        
        this.http.post(`${environment.apiUrl}/websocket/unregister-connection`, {
          user_id: this.currentUserIdForDisconnect,
          socket_id: this.currentSocketId
        }).subscribe({
          next: (response: any) => {
          },
          error: (error) => {
            // Fallback to simple unregister
            this.unregisterSimpleConnection();
          }
        });
      } else {
        // Use simple unregister if no user_id available
        this.unregisterSimpleConnection();
      }
      
      this.currentSocketId = null;
      this.currentUserIdForDisconnect = null;
    } else {
    }
  }

  /**
   * Simple unregister connection (fallback method)
   */
  private unregisterSimpleConnection(): void {
    if (this.currentSocketId) {
      
      this.http.post(`${environment.apiUrl}/websocket/unregister-simple-connection`, {
        socket_id: this.currentSocketId
      }).subscribe({
        next: (response: any) => {
        },
        error: (error) => {
        }
      });
    }
  }

}
